import { useState, useEffect } from 'react';
import type { PoolScenario } from '../types';

export const MAX_SCENARIOS = 20;
const SCENARIOS_STORAGE_KEY = 'zfs-calculator-scenarios';
const CURRENT_CONFIG_KEY = 'zfs-calculator-current-config';

/**
 * Custom hook for managing pool scenarios with localStorage persistence.
 * Automatically loads scenarios on mount and saves to localStorage on changes.
 * Enforces a maximum of 20 scenarios, deleting the oldest when limit exceeded.
 */
export function useScenarios() {
  const [scenarios, setScenarios] = useState<PoolScenario[]>([]);

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(SCENARIOS_STORAGE_KEY);
      if (stored) {
        setScenarios(JSON.parse(stored));
      }
    } catch (e) {
      console.error('Failed to load scenarios from localStorage:', e);
    }
  }, []);

  // Save to localStorage whenever scenarios change
  const saveScenarios = (newScenarios: PoolScenario[]) => {
    // Enforce max scenarios limit - delete oldest if needed
    let trimmedScenarios = newScenarios;
    if (newScenarios.length > MAX_SCENARIOS) {
      trimmedScenarios = newScenarios.slice(-MAX_SCENARIOS);
      console.warn(
        `Scenario limit (${MAX_SCENARIOS}) exceeded. Oldest scenarios have been removed.`
      );
    }

    setScenarios(trimmedScenarios);
    try {
      localStorage.setItem(SCENARIOS_STORAGE_KEY, JSON.stringify(trimmedScenarios));
    } catch (e) {
      if (e instanceof Error && e.name === 'QuotaExceededError') {
        console.error('localStorage quota exceeded. Unable to save scenarios.');
      } else {
        console.error('Failed to save scenarios to localStorage:', e);
      }
    }
  };

  return { scenarios, setScenarios: saveScenarios };
}

/**
 * Custom hook for persisting current configuration to localStorage.
 * Automatically loads on mount and saves on changes.
 * Use this to preserve the user's working configuration across page refreshes.
 */
export function useCurrentConfig<T>(defaultValue: T, storageKey: string = CURRENT_CONFIG_KEY) {
  const [config, setConfigState] = useState<T>(defaultValue);

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Merge with defaults to handle new fields in future versions
        setConfigState({ ...defaultValue, ...parsed });
      }
    } catch (e) {
      console.error(`Failed to load config from localStorage (${storageKey}):`, e);
    }
  }, [storageKey]); // Only run on mount

  // Save to localStorage whenever config changes
  const setConfig = (newConfig: T | ((prev: T) => T)) => {
    const updatedConfig = typeof newConfig === 'function' ? (newConfig as (prev: T) => T)(config) : newConfig;
    setConfigState(updatedConfig);

    try {
      localStorage.setItem(storageKey, JSON.stringify(updatedConfig));
    } catch (e) {
      if (e instanceof Error && e.name === 'QuotaExceededError') {
        console.error('localStorage quota exceeded. Unable to save configuration.');
      } else {
        console.error(`Failed to save config to localStorage (${storageKey}):`, e);
      }
    }
  };

  return [config, setConfig] as const;
}
